import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { LoadFailed } from "@/components/LoadState";
import { Crosshair } from "@/components/Crosshair";
import { toast } from "@/lib/toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tag, Plus, Trash2, Pencil, Percent, DollarSign, Power } from "lucide-react";

const EMPTY = { code: "", kind: "percent", value: 10, active: true, first_purchase_only: true, per_user_once: true, max_redemptions: "", expires_at: "", note: "" };

function CodeDialog({ initial, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...(initial || {}), max_redemptions: initial?.max_redemptions ?? "", expires_at: initial?.expires_at ? initial.expires_at.slice(0, 10) : "" });
  const [busy, setBusy] = useState(false);
  const editing = !!initial?.promo_id;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async () => {
    setBusy(true);
    const payload = {
      code: f.code, kind: f.kind, value: Number(f.value), active: f.active,
      first_purchase_only: f.first_purchase_only, per_user_once: f.per_user_once,
      max_redemptions: f.max_redemptions === "" ? null : Number(f.max_redemptions),
      expires_at: f.expires_at ? new Date(f.expires_at + "T23:59:59").toISOString() : null,
      note: f.note || null,
    };
    try {
      if (editing) await api.put(`/admin/promo-codes/${initial.promo_id}`, payload);
      else await api.post("/admin/promo-codes", payload);
      toast.success(editing ? "Promo code updated." : "Promo code created.");
      onSaved();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const inputCls = "w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-mono text-sm";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface border-sniper text-foreground max-w-lg" data-testid="promo-dialog">
        <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2"><Tag className="w-5 h-5 text-olive-bright" /> {editing ? "Edit" : "New"} Promo Code</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="font-mono text-[11px] uppercase text-secondary-foreground">Code</label>
            <input value={f.code} onChange={set("code")} data-testid="promo-code-input" className={`${inputCls} uppercase`} placeholder="WELCOME10" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[11px] uppercase text-secondary-foreground">Type</label>
              <select value={f.kind} onChange={set("kind")} data-testid="promo-kind" className={inputCls}>
                <option value="percent">Percent %</option>
                <option value="fixed">Fixed $</option>
              </select>
            </div>
            <div>
              <label className="font-mono text-[11px] uppercase text-secondary-foreground">Value</label>
              <input type="number" min="0" step="0.01" value={f.value} onChange={set("value")} data-testid="promo-value" className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[11px] uppercase text-secondary-foreground">Max redemptions</label>
              <input type="number" min="1" value={f.max_redemptions} onChange={set("max_redemptions")} data-testid="promo-max" className={inputCls} placeholder="unlimited" />
            </div>
            <div>
              <label className="font-mono text-[11px] uppercase text-secondary-foreground">Expires</label>
              <input type="date" value={f.expires_at} onChange={set("expires_at")} data-testid="promo-expires" className={inputCls} />
            </div>
          </div>
          <div className="flex flex-col gap-2 pt-1">
            <label className="flex items-center gap-2 font-mono text-xs uppercase text-secondary-foreground"><input type="checkbox" checked={f.active} onChange={set("active")} data-testid="promo-active" /> Active</label>
            <label className="flex items-center gap-2 font-mono text-xs uppercase text-secondary-foreground"><input type="checkbox" checked={f.first_purchase_only} onChange={set("first_purchase_only")} data-testid="promo-first-only" /> First purchase only</label>
            <label className="flex items-center gap-2 font-mono text-xs uppercase text-secondary-foreground"><input type="checkbox" checked={f.per_user_once} onChange={set("per_user_once")} data-testid="promo-per-user-once" /> One use per buyer</label>
          </div>
          <div>
            <label className="font-mono text-[11px] uppercase text-secondary-foreground">Note (internal)</label>
            <input value={f.note} onChange={set("note")} data-testid="promo-note" className={inputCls} />
          </div>
          <p className="text-[11px] font-mono text-muted-foreground border border-sniper p-2.5">
            The discount is funded from the platform fee and clamped so it never exceeds the fee on an order — seller payout is never reduced.
          </p>
          <button onClick={save} disabled={busy || !f.code} data-testid="promo-save"
            className="w-full bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase py-2.5 transition-colors">
            {busy ? "Saving…" : editing ? "Save changes" : "Create code"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPromoCodes() {
  const [codes, setCodes] = useState([]);
  const [dialog, setDialog] = useState(null); // null | {} | code
  const [loadErr, setLoadErr] = useState(null);

  const load = () => { setLoadErr(null); return api.get("/admin/promo-codes").then(({ data }) => setCodes(data.promo_codes)).catch((err) => setLoadErr(formatError(err?.response?.data?.detail) || "Couldn't load promo codes.")); };
  useEffect(() => { load(); }, []);

  const remove = async (c) => {
    if (!window.confirm(`Delete promo code ${c.code}?`)) return;
    try { await api.delete(`/admin/promo-codes/${c.promo_id}`); toast.success("Deleted."); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <div className="text-foreground" data-testid="admin-promo-codes">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-heading text-4xl font-bold uppercase tracking-tight flex items-center gap-2"><Tag className="w-7 h-7 text-olive-bright" /> Promo Codes</h2>
        <button onClick={() => setDialog({})} data-testid="promo-new-btn" className="inline-flex items-center gap-2 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase px-5 py-2.5 transition-colors"><Plus className="w-4 h-4" /> New Code</button>
      </div>
      <p className="text-sm font-body text-secondary-foreground mt-2">Buyer discounts funded from the platform fee (clamped to the fee). Applied at marketplace checkout.</p>
      <Crosshair className="my-5" />

      {loadErr ? (
        <div className="border border-sniper bg-surface" data-testid="promo-table">
          <LoadFailed reason={loadErr} onRetry={load} testid="promo-load-failed" />
        </div>
      ) : codes.length === 0 ? (
        <div className="border border-sniper bg-surface p-10 text-center" data-testid="promo-empty">
          <Tag className="w-8 h-8 text-muted-foreground/40 mx-auto mb-3" />
          <p className="font-heading uppercase tracking-tight text-lg text-foreground">No promo codes yet</p>
          <p className="font-mono text-[11px] text-muted-foreground mt-1 max-w-md mx-auto">Buyer discounts funded from the platform fee (clamped to the fee), applied at marketplace checkout. Create your first code to get started.</p>
          <button onClick={() => setDialog({})} data-testid="promo-empty-new" className="mt-4 inline-flex items-center gap-1.5 border border-olive/50 text-olive-bright hover:bg-olive/10 font-mono uppercase text-[11px] px-4 py-2 transition-colors"><Plus className="w-3.5 h-3.5" /> New code</button>
        </div>
      ) : (
      <div className="border border-sniper bg-surface overflow-x-auto" data-testid="promo-table">
        <table className="w-full font-mono text-xs">
          <thead><tr className="text-left text-muted-foreground uppercase text-[10px] border-b border-sniper">
            <th className="px-3 py-2.5">Code</th><th className="px-3 py-2.5">Discount</th><th className="px-3 py-2.5">Rules</th><th className="px-3 py-2.5">Used</th><th className="px-3 py-2.5">Status</th><th className="px-3 py-2.5"></th>
          </tr></thead>
          <tbody className="divide-y divide-sniper">
            {codes.map((c) => (
              <tr key={c.promo_id} className="text-secondary-foreground" data-testid={`promo-row-${c.code}`}>
                <td className="px-3 py-2.5 text-foreground font-bold tracking-wide">{c.code}</td>
                <td className="px-3 py-2.5 flex items-center gap-1">{c.kind === "percent" ? <><Percent className="w-3 h-3" />{c.value}</> : <><DollarSign className="w-3 h-3" />{c.value}</>}</td>
                <td className="px-3 py-2.5">{[c.first_purchase_only && "1st purchase", c.per_user_once && "1/buyer", c.max_redemptions ? `max ${c.max_redemptions}` : null, c.expires_at ? `exp ${c.expires_at.slice(0, 10)}` : null].filter(Boolean).join(" · ") || "—"}</td>
                <td className="px-3 py-2.5">{c.redeemed_count || 0}</td>
                <td className="px-3 py-2.5">{c.active ? <span className="text-olive-bright inline-flex items-center gap-1"><Power className="w-3 h-3" /> active</span> : <span className="text-muted-foreground">off</span>}</td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  <button onClick={() => setDialog(c)} data-testid={`promo-edit-${c.code}`} className="p-1.5 text-muted-foreground hover:text-foreground"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(c)} data-testid={`promo-delete-${c.code}`} className="p-1.5 text-muted-foreground hover:text-blaze"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {dialog && <CodeDialog initial={dialog.promo_id ? dialog : null} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />}
    </div>
  );
}
